import { addClassNamesToElement } from "@lexical/utils"
import {
  $applyNodeReplacement,
  $createParagraphNode,
  $isElementNode,
  $isLineBreakNode,
  $isTextNode,
  ElementNode,
} from "lexical"
import { COLUMN_WIDTH, PIXEL_VALUE_REG_EXP } from "./constants"

export const TableCellHeaderStates = {
  BOTH: 3,
  COLUMN: 2,
  NO_STATUS: 0,
  ROW: 1,
}

/** @noInheritDoc */
export class TableCellNode extends ElementNode {
  /** @internal */
  __colSpan
  /** @internal */
  __rowSpan
  /** @internal */
  __headerState
  /** @internal */
  __width
  /** @internal */
  __backgroundColor

  static getType() {
    return "tablecell"
  }

  static clone(node) {
    const cellNode = new TableCellNode(node.__headerState, node.__colSpan, node.__width, node.__key)
    cellNode.__rowSpan = node.__rowSpan
    cellNode.__backgroundColor = node.__backgroundColor
    return cellNode
  }

  static importDOM() {
    return {
      td: (node) => ({
        conversion: $convertTableCellNodeElement,
        priority: 0,
      }),
      th: (node) => ({
        conversion: $convertTableCellNodeElement,
        priority: 0,
      }),
    }
  }

  static importJSON(serializedNode) {
    const colSpan = serializedNode.colSpan || 1
    const rowSpan = serializedNode.rowSpan || 1
    const cellNode = $createTableCellNode(
      serializedNode.headerState,
      colSpan,
      serializedNode.width || undefined,
    )
    cellNode.__rowSpan = rowSpan
    cellNode.__backgroundColor = serializedNode.backgroundColor || null
    return cellNode
  }

  constructor(headerState = TableCellHeaderStates.NO_STATUS, colSpan = 1, width, key) {
    super(key)
    this.__colSpan = colSpan
    this.__rowSpan = 1
    this.__headerState = headerState
    this.__width = width
    this.__backgroundColor = null
  }

  createDOM(config) {
    const element = document.createElement(this.getTag())

    if (this.__width) {
      element.style.width = `${this.__width}px`
    }
    if (this.__colSpan > 1) {
      element.colSpan = this.__colSpan
    }
    if (this.__rowSpan > 1) {
      element.rowSpan = this.__rowSpan
    }
    if (this.__backgroundColor !== null) {
      element.style.backgroundColor = this.__backgroundColor
    }

    addClassNamesToElement(
      element,
      config.theme.tableCell,
      this.hasHeader() && config.theme.tableCellHeader,
    )

    return element
  }

  exportDOM(editor) {
    const { element } = super.exportDOM(editor)

    if (element) {
      const element_ = element
      element_.style.border = "1px solid black"
      if (this.__colSpan > 1) {
        element_.colSpan = this.__colSpan
      }
      if (this.__rowSpan > 1) {
        element_.rowSpan = this.__rowSpan
      }
      element_.style.width = `${this.getWidth() || COLUMN_WIDTH}px`

      element_.style.verticalAlign = "top"
      element_.style.textAlign = "start"

      const backgroundColor = this.getBackgroundColor()
      if (backgroundColor !== null) {
        element_.style.backgroundColor = backgroundColor
      } else if (this.hasHeader()) {
        element_.style.backgroundColor = "#f2f3f5"
      }
    }

    return {
      element,
    }
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      backgroundColor: this.getBackgroundColor(),
      colSpan: this.__colSpan,
      headerState: this.__headerState,
      rowSpan: this.__rowSpan,
      type: "tablecell",
      width: this.getWidth(),
    }
  }

  getColSpan() {
    return this.__colSpan
  }

  setColSpan(colSpan) {
    this.getWritable().__colSpan = colSpan
    return this
  }

  getRowSpan() {
    return this.__rowSpan
  }

  setRowSpan(rowSpan) {
    this.getWritable().__rowSpan = rowSpan
    return this
  }

  getTag() {
    return this.hasHeader() ? "th" : "td"
  }

  setHeaderStyles(headerState) {
    const self = this.getWritable()
    self.__headerState = headerState
    return this.__headerState
  }

  getHeaderStyles() {
    return this.getLatest().__headerState
  }

  setWidth(width) {
    const self = this.getWritable()
    self.__width = width
    return this.__width
  }

  getWidth() {
    return this.getLatest().__width
  }

  getBackgroundColor() {
    return this.getLatest().__backgroundColor
  }

  setBackgroundColor(newBackgroundColor) {
    this.getWritable().__backgroundColor = newBackgroundColor
  }

  toggleHeaderStyle(headerStateToToggle) {
    const self = this.getWritable()

    if ((self.__headerState & headerStateToToggle) === headerStateToToggle) {
      self.__headerState -= headerStateToToggle
    } else {
      self.__headerState += headerStateToToggle
    }

    return self
  }

  hasHeaderState(headerState) {
    return (this.getHeaderStyles() & headerState) === headerState
  }

  hasHeader() {
    return this.getLatest().__headerState !== TableCellHeaderStates.NO_STATUS
  }

  updateDOM(prevNode) {
    return (
      prevNode.__headerState !== this.__headerState ||
      prevNode.__width !== this.__width ||
      prevNode.__colSpan !== this.__colSpan ||
      prevNode.__rowSpan !== this.__rowSpan ||
      prevNode.__backgroundColor !== this.__backgroundColor
    )
  }

  isShadowRoot() {
    return true
  }

  collapseAtStart() {
    return true
  }

  canBeEmpty() {
    return false
  }

  canIndent() {
    return false
  }
}

export function $convertTableCellNodeElement(domNode) {
  const domNode_ = domNode
  const nodeName = domNode.nodeName.toLowerCase()

  let width = undefined

  if (PIXEL_VALUE_REG_EXP.test(domNode_.style.width)) {
    width = parseFloat(domNode_.style.width)
  }

  const tableCellNode = $createTableCellNode(
    nodeName === "th" ? TableCellHeaderStates.ROW : TableCellHeaderStates.NO_STATUS,
    domNode_.colSpan,
    width,
  )

  tableCellNode.__rowSpan = domNode_.rowSpan
  const backgroundColor = domNode_.style.backgroundColor
  if (backgroundColor !== "") {
    tableCellNode.__backgroundColor = backgroundColor
  }

  const style = domNode_.style
  const textDecoration = style.textDecoration.split(" ")
  const hasBoldFontWeight = style.fontWeight === "700" || style.fontWeight === "bold"
  const hasLinethroughTextDecoration = textDecoration.includes("line-through")
  const hasItalicFontStyle = style.fontStyle === "italic"
  const hasUnderlineTextDecoration = textDecoration.includes("underline")
  return {
    after: (childLexicalNodes) => {
      if (childLexicalNodes.length === 0) {
        childLexicalNodes.push($createParagraphNode())
      }
      return childLexicalNodes
    },
    forChild: (lexicalNode, parentLexicalNode) => {
      if ($isTableCellNode(parentLexicalNode) && !$isElementNode(lexicalNode)) {
        const paragraphNode = $createParagraphNode()
        if ($isLineBreakNode(lexicalNode) && lexicalNode.getTextContent() === "\n") {
          return null
        }
        if ($isTextNode(lexicalNode)) {
          if (hasBoldFontWeight) {
            lexicalNode.toggleFormat("bold")
          }
          if (hasLinethroughTextDecoration) {
            lexicalNode.toggleFormat("strikethrough")
          }
          if (hasItalicFontStyle) {
            lexicalNode.toggleFormat("italic")
          }
          if (hasUnderlineTextDecoration) {
            lexicalNode.toggleFormat("underline")
          }
        }
        paragraphNode.append(lexicalNode)
        return paragraphNode
      }

      return lexicalNode
    },
    node: tableCellNode,
  }
}

export function $createTableCellNode(headerState, colSpan = 1, width) {
  return $applyNodeReplacement(new TableCellNode(headerState, colSpan, width))
}

export function $isTableCellNode(node) {
  return node instanceof TableCellNode
}
